import React from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  Container,
  Image,
  Flex,
  SimpleGrid,
  Icon,
  Badge,
  HStack
} from '@chakra-ui/react';
import { FaLightbulb, FaRocket, FaHandsHelping, FaChessKnight, FaCompass, FaGraduationCap } from 'react-icons/fa';
import { motion } from 'framer-motion';
import Header from '../layout/Header';
import Footer from '../layout/Footer';

const MotionBox = motion(Box);

const VisionPage = () => {
  const primaryColor = '#F2C919';
  const secondaryColor = '#2C3E50';

  const values = [
    {
      icon: FaLightbulb,
      title: "الابتكار الرقمي",
      description: "نستخدم أحدث وسائل التكنولوجيا لجعل التعليم تجربة ممتعة وتفاعلية تتجاوز حدود الجدران المدرسية."
    },
    {
      icon: FaHandsHelping,
      title: "مبدأ التكافؤ",
      description: "نسعى لجعل التعليم الجيد متاحاً لكل تلميذ تونسي مهما كان موقعه الجغرافي أو ظروفه الاجتماعية."
    },
    {
      icon: FaRocket,
      title: "التميز الدراسي",
      description: "هدفنا ليس فقط النجاح، بل التميز والحصول على أفضل النتائج في المناظرات والامتحانات الوطنية."
    }
  ];

  return (
    <Box dir="rtl" bg="gray.50">
      <Header />

      {/* Hero Section */}
      <Box
        bgGradient={`linear(to-l, ${secondaryColor}, gray.800)`}
        color="white"
        py={{ base: 20, md: 32 }}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top="-10%"
          left="-5%"
          width="400px"
          height="400px"
          bg={primaryColor}
          opacity="0.05"
          borderRadius="full"
          filter="blur(100px)"
        />

        <Container maxW="1400px" textAlign="center" position="relative" zIndex={1}>
          <VStack spacing={8}>
            <Badge
              bg={primaryColor}
              color={secondaryColor}
              px={6}
              py={2}
              borderRadius="full"
              fontSize="md"
              fontFamily="Cairo, sans-serif"
              fontWeight="800"
            >
              رؤية كوبرا
            </Badge>
            <Heading fontSize={{ base: '4xl', md: '6xl' }} fontFamily="Cairo, sans-serif" fontWeight="900" lineHeight="1.2">
              نحو مستقبل تعليمي <br />
              <Text as="span" color={primaryColor}>أفضل لكل تلميذ</Text>
            </Heading>
            <Text fontSize="xl" maxW="3xl" mx="auto" fontFamily="Tajawal, sans-serif" color="whiteAlpha.900" lineHeight="1.8">
              في منصة كوبرا، نحن لا نقدم فقط دروساً، بل نبني مستقبلاً. رؤيتنا هي أن نكون الشريك الأول لكل تلميذ في رحلة التميز والنجاح.
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Content Sections */}
      <Container maxW="1400px" py={{ base: 20, md: 32 }}>
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={20} alignItems="center" mb={32}>
          <MotionBox
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <VStack align="flex-start" spacing={8}>
              <HStack spacing={4}>
                <Box p={3} bg={`${primaryColor}15`} borderRadius="xl">
                  <Icon as={FaCompass} color={primaryColor} boxSize={8} />
                </Box>
                <Heading size="xl" fontFamily="Cairo, sans-serif" fontWeight="900" color={secondaryColor}>
                  لماذا نحن هنا؟
                </Heading>
              </HStack>
              <Text fontSize="lg" fontFamily="Tajawal, sans-serif" color="gray.600" lineHeight="1.8">
                ولدت منصة كوبرا من رحم الحاجة إلى تعليم عصري يواكب تطلعات الجيل الجديد من التلاميذ التونسيين. نحن هنا لسد الفجوة بين المناهج التقليدية والأساليب الحديثة، وتوفير بيئة تعليمية محفزة تساعد التلميذ على اكتشاف قدراته الكامنة.
              </Text>
              <Text fontSize="lg" fontFamily="Tajawal, sans-serif" color="gray.600" lineHeight="1.8">
                نحن نؤمن بأن التعليم الرقمي ليس مجرد بديل، بل هو وسيلة فعالة لكسر كل الحواجز التي تواجه التلميذ في المدرسة التقليدية، مما يسمح له بالتعلم بالسرعة التي تناسبه وفي الوقت الذي يختار.
              </Text>
            </VStack>
          </MotionBox>
          <MotionBox
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Image
              src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800"
              alt="رؤية تعليمية"
              borderRadius="3xl"
              boxShadow="2xl"
              fallbackSrc="https://ui-avatars.com/api/?name=Vision&background=F2C919&color=2C3E50&size=800"
            />
          </MotionBox>
        </SimpleGrid>

        <VStack spacing={20}>
          <VStack spacing={4} textAlign="center" maxW="800px">
            <Heading fontSize={{ base: '3xl', md: '5xl' }} fontFamily="Cairo, sans-serif" fontWeight="900" color={secondaryColor}>
              القيم التي تقودنا
            </Heading>
            <Text fontSize="lg" fontFamily="Tajawal, sans-serif" color="gray.600">
              ترتكز رؤية كوبرا على مجموعة من القيم الأساسية التي تضمن جودة ما نقدمه لكل تلميذ.
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} w="100%">
            {values.map((v, i) => (
              <Box
                key={i}
                bg="white"
                p={10}
                borderRadius="2xl"
                boxShadow="xl"
                textAlign="center"
                border="1px solid"
                borderColor="gray.100"
                _hover={{ transform: 'translateY(-10px)', borderColor: primaryColor }}
                transition="all 0.3s"
              >
                <VStack spacing={6}>
                  <Box p={5} bg={`${primaryColor}15`} borderRadius="2xl" color={primaryColor}>
                    <Icon as={v.icon} boxSize={10} />
                  </Box>
                  <Heading size="md" fontFamily="Cairo, sans-serif" fontWeight="900" color={secondaryColor}>
                    {v.title}
                  </Heading>
                  <Text fontFamily="Tajawal, sans-serif" color="gray.600" lineHeight="1.7">
                    {v.description}
                  </Text>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </VStack>

        <Box
          mt={32}
          bg={primaryColor}
          p={{ base: 10, md: 20 }}
          borderRadius="3xl"
          textAlign="center"
          boxShadow="2xl"
        >
          <VStack spacing={8}>
            <Icon as={FaGraduationCap} boxSize={16} color={secondaryColor} />
            <Heading size="xl" fontFamily="Cairo, sans-serif" fontWeight="900" color={secondaryColor}>
              مستقبلنا يبدأ بنجاح تلاميذنا
            </Heading>
            <Text fontSize="xl" fontFamily="Tajawal, sans-serif" color={secondaryColor} fontWeight="700" maxW="800px">
              نعمل باستمرار على توسيع خدماتنا لتشمل المزيد من المواد والمستويات، ولنكون دائماً الخيار الأول والوحيد للعائلة التونسية في مجال التعليم الإلكتروني.
            </Text>
          </VStack>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default VisionPage;
